import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Package,
  Coins,
  DollarSign,
  Check,
  X,
  Edit2,
  Save,
  Loader2,
  RefreshCw,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

const AdminPackagesPage = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/api/admin/packages`);
      setPackages(response.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch packages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const handleEditPrice = (pkg) => {
    setEditingId(pkg.id);
    setEditPrice(pkg.price.toString());
  };

  const handleSavePrice = async (packageId) => {
    const newPrice = parseFloat(editPrice);
    if (isNaN(newPrice) || newPrice < 0) {
      setError("Please enter a valid price");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await axios.put(
        `${API}/api/admin/packages/${packageId}`,
        { price: newPrice }
      );
      
      setPackages(packages.map(pkg => 
        pkg.id === packageId ? { ...pkg, price: newPrice } : pkg
      ));
      setEditingId(null);
      setSuccessMessage("Price updated successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update price");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (pkg) => {
    setSaving(true);
    setError(null);

    try {
      await axios.put(
        `${API}/api/admin/packages/${pkg.id}`,
        { active: !pkg.active }
      );
      
      setPackages(packages.map(p => 
        p.id === pkg.id ? { ...p, active: !p.active } : p
      ));
      setSuccessMessage(`Package ${!pkg.active ? 'enabled' : 'disabled'} successfully!`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update package");
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const formatCredits = (credits) => {
    return new Intl.NumberFormat('en-US').format(credits);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin")}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Dashboard</span>
              </button>
              <div className="h-6 w-px bg-white/20" />
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Package className="w-5 h-5 text-amber-400" />
                Credit Packages
              </h1>
            </div>
            
            <button
              onClick={fetchPackages}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              data-testid="refresh-packages"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6 text-green-400 flex items-center gap-2"
          >
            <Check className="w-5 h-5" />
            {successMessage}
          </motion.div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 text-red-400 flex items-center gap-2">
            <X className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Info Card */}
        <div className="bg-gradient-to-r from-amber-500/10 to-purple-500/10 border border-amber-500/20 rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
            <Coins className="w-5 h-5 text-amber-400" />
            Manage Credit Packages
          </h2>
          <p className="text-gray-400">
            Set the prices for credit packages that fans can purchase. 
            Toggle packages on/off to control availability.
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : (
          /* Packages Grid */
          <div className="grid gap-4">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white/5 backdrop-blur-xl rounded-xl border transition-colors ${
                  pkg.active 
                    ? "border-white/10 hover:border-white/20" 
                    : "border-red-500/20 opacity-60"
                }`}
                data-testid={`package-${pkg.id}`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    {/* Package Info */}
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                        pkg.active 
                          ? "bg-gradient-to-br from-amber-500/20 to-amber-600/20" 
                          : "bg-gray-700/50"
                      }`}>
                        <Coins className={`w-7 h-7 ${pkg.active ? "text-amber-400" : "text-gray-500"}`} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-white">
                          {formatCredits(pkg.credits)} <span className="text-sm font-normal text-gray-400">credits</span>
                        </h3>
                        <p className="text-gray-500 text-sm">
                          {pkg.active ? "Active" : "Disabled"}
                        </p>
                      </div>
                    </div>

                    {/* Price and Actions */}
                    <div className="flex items-center gap-4">
                      {editingId === pkg.id ? (
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                            <input
                              type="number"
                              value={editPrice}
                              onChange={(e) => setEditPrice(e.target.value)}
                              className="w-28 pl-8 pr-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-amber-500"
                              step="0.01"
                              min="0"
                              autoFocus
                            />
                          </div>
                          <button
                            onClick={() => handleSavePrice(pkg.id)}
                            disabled={saving}
                            className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors disabled:opacity-50"
                          >
                            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-400">{formatPrice(pkg.price)}</p>
                            <p className="text-gray-500 text-xs">
                              {(pkg.price / pkg.credits * 100).toFixed(2)}¢ per credit
                            </p>
                          </div>
                          <button
                            onClick={() => handleEditPrice(pkg)}
                            className="p-2 bg-white/10 text-gray-400 rounded-lg hover:bg-white/20 hover:text-white transition-colors"
                            title="Edit price"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(pkg)}
                            disabled={saving}
                            className={`p-2 rounded-lg transition-colors ${
                              pkg.active 
                                ? "bg-green-500/20 text-green-400 hover:bg-green-500/30" 
                                : "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            }`}
                            title={pkg.active ? "Disable package" : "Enable package"}
                          >
                            {pkg.active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPackagesPage;
